import { defineStore } from 'pinia';
import { adminApi } from '@/api/admin';
import type { ConfigFileDto, ValidationResultDto } from '@/api/types';
import { ApiError } from '@/api/client';

// Reformat a dirty draft before saving so every saved file has consistent 2-space JSON
// indentation regardless of how the operator typed it. `JSON.parse` + `JSON.stringify` is a
// no-op for already-formatted content. If the draft isn't valid JSON — invalid mid-edit text
// or a JSON-with-comments file (jsonc) that the standard parser rejects — the original text
// is returned untouched, so the operator's work-in-progress and any inline comments survive
// the save (and the server-side JsonValidator still surfaces structural errors). Preserves
// the original trailing-newline state to avoid silently introducing diff churn against
// files that don't yet terminate with one.
function tryFormatJsonDraft(text: string): string {
  try {
    const parsed = JSON.parse(text);
    const hadTrailingNewline = text.endsWith('\n');
    const formatted = JSON.stringify(parsed, null, 2);
    return hadTrailingNewline ? formatted + '\n' : formatted;
  } catch {
    return text;
  }
}

// Multi-file editor state. Each path the operator opens becomes an entry in `open`, in
// insertion order (`order` drives the tab strip). `activePath` names the currently-shown
// file. The store keeps single-file aliases (`current`, `draft`, etc.) so single-pane
// callers like LayeredSlotEditor can stay unchanged — those resolve to the active tab.
//
// Per-file model state (undo, cursor, scroll) lives in the JsonEditor component, keyed by
// the file's path. When a tab is closed here the editor disposes the matching model.

// A live broadcast told us a different operator saved this file. `newContents` is the fresh
// disk content already fetched; the modal can render it side-by-side with our draft via the
// existing DiffViewer. `incomingChange` is non-null only when our draft was dirty at the
// time we received the broadcast — a clean editor silently catches up without prompting.
export interface IncomingFileChange {
  newSha256: string;
  newContents: string;
  actor: string;
}

interface OpenFile {
  // Original server state — last loaded or last saved.
  server: ConfigFileDto;
  // Current editor contents. May differ from server.contents — that's "dirty".
  draft: string;
  // Per-file flags. Validation results and conflict state are scoped to the file they apply
  // to, not stamped globally, so switching tabs doesn't show stale banners.
  validation: ValidationResultDto | null;
  conflict: boolean;
  loadError: string | null;
  saveError: string | null;
  incomingChange: IncomingFileChange | null;
}

// One-shot signal: a remote write silently caught up an open clean file. AppShell watches
// this and emits a Toast so the operator sees that their editor changed under them
// (otherwise the silent path is too silent). Bumped to a fresh object every time so a
// repeat-update on the same path still triggers the watcher.
export interface ExternalReloadNotice {
  path: string;
  actor: string;
  at: number;
}

interface FilesState {
  open: Record<string, OpenFile>;
  order: string[];
  activePath: string | null;
  saving: boolean;
  loading: boolean;
  validating: boolean;
  lastExternalReload: ExternalReloadNotice | null;
  // Recovered drafts waiting to be re-applied the next time their path is opened. Draft
  // recovery can't just setDraft() before navigating: some editor views (ServiceEditorView)
  // call closeAll()+openFile() on mount, which would wipe a pre-staged draft. Instead the
  // recovered text is parked here and openFile re-applies it whenever the file loads — so
  // it survives any number of close/open cycles the destination view performs.
  pendingRecovered: Record<string, string>;
}

export const useFilesStore = defineStore('files', {
  state: (): FilesState => ({
    open: {},
    order: [],
    activePath: null,
    saving: false,
    loading: false,
    validating: false,
    lastExternalReload: null,
    pendingRecovered: {},
  }),
  getters: {
    active(state): OpenFile | null {
      return state.activePath ? state.open[state.activePath] ?? null : null;
    },
    activeIsDirty(): boolean {
      const a = this.active;
      return a !== null && a.draft !== a.server.contents;
    },
    anyDirty(state): boolean {
      return Object.values(state.open).some((f) => f.draft !== f.server.contents);
    },
    dirtyPaths(state): string[] {
      return state.order.filter((p) => {
        const f = state.open[p];
        return f && f.draft !== f.server.contents;
      });
    },
    tabs(state): { path: string; dirty: boolean; active: boolean }[] {
      return state.order.map((p) => {
        const f = state.open[p]!;
        return {
          path: p,
          dirty: f.draft !== f.server.contents,
          active: state.activePath === p,
        };
      });
    },
    // Single-file aliases used by views that don't surface tabs (LayeredSlotEditor and the
    // old single-file editing patterns). They all proxy to the active tab.
    current(): ConfigFileDto | null {
      return this.active?.server ?? null;
    },
    draft(): string {
      return this.active?.draft ?? '';
    },
    isDirty(): boolean {
      return this.activeIsDirty;
    },
    saveError(): string | null {
      return this.active?.saveError ?? null;
    },
    loadError(): string | null {
      return this.active?.loadError ?? null;
    },
    conflict(): boolean {
      return this.active?.conflict ?? false;
    },
    validation(): ValidationResultDto | null {
      return this.active?.validation ?? null;
    },
  },
  actions: {
    // Park a recovered draft for `path`, and apply it now if the file is already open.
    // The parked entry is intentionally NOT consumed on apply (see applyPendingRecovered)
    // so it survives the closeAll()+openFile() churn ServiceEditorView does on mount.
    stageRecovered(path: string, draft: string) {
      this.pendingRecovered[path] = draft;
      this.applyPendingRecovered(path);
    },

    // Lay a parked recovered draft over the open file. Re-applies on EVERY openFile
    // (not one-shot): some editor views closeAll()+openFile() on mount, and a one-shot
    // consume would lose to that race. It stops re-clobbering once the operator takes a
    // real action — setDraft (a genuine edit), save, discard, or close — each of which
    // clears the parked entry. closeAll() deliberately does NOT clear it (it's a
    // transient reopen, not user intent).
    applyPendingRecovered(path: string) {
      const pending = this.pendingRecovered[path];
      if (pending === undefined) return;
      const f = this.open[path];
      if (!f) return;
      f.draft = pending;
      f.validation = null;
    },

    clearPendingRecovered(path: string) {
      delete this.pendingRecovered[path];
    },

    // Open a file. If already open, just activate. Otherwise fetch, add to the open map,
    // append to the tab order, and activate.
    async openFile(path: string) {
      if (this.open[path]) {
        this.activePath = path;
        this.applyPendingRecovered(path);
        return;
      }
      this.loading = true;
      try {
        const file = await adminApi.getFile(path);
        this.open[path] = {
          server: file,
          draft: file.contents,
          validation: null,
          conflict: false,
          loadError: null,
          saveError: null,
          incomingChange: null,
        };
        if (!this.order.includes(path)) {
          this.order.push(path);
        }
        this.activePath = path;
        this.applyPendingRecovered(path);
      } catch (e) {
        // Add a placeholder entry so the tab still shows up with its error. Operator can
        // close it manually.
        this.open[path] = {
          server: { path, contents: '', sha256: '', lastModifiedUtc: '' },
          draft: '',
          validation: null,
          conflict: false,
          loadError: (e as Error).message,
          saveError: null,
          incomingChange: null,
        };
        if (!this.order.includes(path)) {
          this.order.push(path);
        }
        this.activePath = path;
      } finally {
        this.loading = false;
      }
    },

    setActive(path: string) {
      if (this.open[path]) {
        this.activePath = path;
      }
    },

    close(path: string) {
      if (!this.open[path]) return;
      delete this.open[path];
      // Closing a tab is user intent — drop any parked recovered draft so reopening
      // the file later starts from server state, not the recovered text.
      this.clearPendingRecovered(path);
      const idx = this.order.indexOf(path);
      if (idx >= 0) this.order.splice(idx, 1);
      if (this.activePath === path) {
        this.activePath = this.order[idx] ?? this.order[idx - 1] ?? this.order[0] ?? null;
      }
    },

    closeAll() {
      this.open = {};
      this.order = [];
      this.activePath = null;
    },

    setDraft(value: string, path?: string) {
      const target = path ?? this.activePath;
      if (!target) return;
      const f = this.open[target];
      if (!f) return;
      f.draft = value;
      f.validation = null;
      // A genuine edit (value diverges from the parked recovered text) means the
      // operator has taken control — stop re-applying it on subsequent opens. An
      // editor echo of the identical recovered text is ignored so it doesn't clear
      // the parked entry before a closeAll()+openFile() cycle can re-apply it.
      const pending = this.pendingRecovered[target];
      if (pending !== undefined && value !== pending) {
        this.clearPendingRecovered(target);
      }
    },

    async validate(path?: string) {
      const target = path ?? this.activePath;
      if (!target) return;
      const f = this.open[target];
      if (!f) return;
      f.validation = await adminApi.validateFile({
        path: f.server.path,
        contents: f.draft,
        expectedSha256: null,
      });
    },

    async save(path?: string): Promise<boolean> {
      const target = path ?? this.activePath;
      if (!target) return false;
      const f = this.open[target];
      if (!f) return false;
      this.saving = true;
      f.saveError = null;
      f.conflict = false;
      // Reformat the draft before sending. Updating `draft` in-place propagates through the
      // Pinia getter to the JsonEditor's `:model-value` binding, so the editor reflects the
      // formatted text immediately (and the operator sees what was actually saved). Cleared
      // validation because the reformat invalidates any previous server-side check.
      const formatted = tryFormatJsonDraft(f.draft);
      if (formatted !== f.draft) {
        f.draft = formatted;
        f.validation = null;
      }
      try {
        const updated = await adminApi.saveFile({
          path: f.server.path,
          contents: f.draft,
          expectedSha256: f.server.sha256,
        });
        if (!updated) throw new Error('Save returned no body');
        f.server = updated;
        f.draft = updated.contents;
        // Recovered work is now persisted — drop the parked entry.
        this.clearPendingRecovered(target);
        return true;
      } catch (e) {
        if (e instanceof ApiError && e.status === 409) {
          f.conflict = true;
        }
        f.saveError = (e as Error).message;
        return false;
      } finally {
        this.saving = false;
      }
    },

    // Save every dirty file sequentially and report which paths landed cleanly and which
    // failed. Caller (e.g. ServiceEditorView's Save-all toolbar button) emits a single
    // success/failure Toast based on the outcome. Sequential rather than parallel so the
    // server sees writes in a deterministic order and so a 409 on one file doesn't race
    // with a successful save on another.
    async saveAll(): Promise<{ saved: string[]; failed: string[] }> {
      const dirty = this.dirtyPaths.slice();
      const saved: string[] = [];
      const failed: string[] = [];
      for (const path of dirty) {
        const ok = await this.save(path);
        if (ok) saved.push(path);
        else failed.push(path);
      }
      return { saved, failed };
    },

    // Validate every dirty file sequentially. Mirrors saveAll's pattern. Returns split lists
    // so the caller can show a single aggregated toast and decide whether to focus the first
    // invalid file (the most useful UX after a batch validate).
    async validateAll(): Promise<{ valid: string[]; invalid: string[] }> {
      const dirty = this.dirtyPaths.slice();
      const valid: string[] = [];
      const invalid: string[] = [];
      this.validating = true;
      try {
        for (const path of dirty) {
          await this.validate(path);
          const f = this.open[path];
          if (f?.validation?.isValid) valid.push(path);
          else invalid.push(path);
        }
      } finally {
        this.validating = false;
      }
      return { valid, invalid };
    },

    // Reset the active tab's draft back to the last server contents. The single-file
    // counterpart to closeAll — used by callers that "switch context" without closing.
    discard(path?: string) {
      const target = path ?? this.activePath;
      if (!target) return;
      const f = this.open[target];
      if (!f) return;
      f.draft = f.server.contents;
      f.validation = null;
      // Explicit "throw away my changes" — including any parked recovered draft, else
      // the next open would resurrect what the operator just discarded.
      this.clearPendingRecovered(target);
    },

    // Realtime: a different operator's save landed. If we have the file open, fold the new
    // server state in. Clean editor: silent update. Dirty editor: stash on incomingChange so
    // the conflict modal renders. The broadcast carries the new sha256; the caller pre-fetched
    // the contents and hands them in.
    applyExternalFileWrite(path: string, change: IncomingFileChange) {
      const f = this.open[path];
      if (!f) return;
      // Same sha as we last knew: probably our own echo arriving before connectionId stamped
      // outgoing requests, or a no-op rewrite. Either way, nothing to do.
      if (f.server.sha256 === change.newSha256) return;

      const isDirty = f.draft !== f.server.contents;
      if (!isDirty) {
        // Silently catch up; the editor model rebinds because draft changes alongside server.
        f.server = {
          path,
          sha256: change.newSha256,
          contents: change.newContents,
          lastModifiedUtc: new Date().toISOString(),
        };
        f.draft = change.newContents;
        f.validation = null;
        f.incomingChange = null;
        // Signal to AppShell so the operator gets a toast — without it the editor's contents
        // changing under them is mysterious.
        this.lastExternalReload = { path, actor: change.actor, at: Date.now() };
        return;
      }
      f.incomingChange = change;
    },

    // Modal: "Discard my changes" — adopt the incoming contents as the new server + draft.
    discardIncomingChange(path: string) {
      const f = this.open[path];
      if (!f || !f.incomingChange) return;
      const change = f.incomingChange;
      f.server = {
        path,
        sha256: change.newSha256,
        contents: change.newContents,
        lastModifiedUtc: new Date().toISOString(),
      };
      f.draft = change.newContents;
      f.validation = null;
      f.incomingChange = null;
      f.saveError = null;
      f.conflict = false;
    },

    // Modal: "Keep my changes" — bump server.sha256 to the incoming value so our next save
    // overwrites the other operator's work. Draft stays untouched.
    acceptIncomingChange(path: string) {
      const f = this.open[path];
      if (!f || !f.incomingChange) return;
      f.server = {
        ...f.server,
        sha256: f.incomingChange.newSha256,
      };
      f.incomingChange = null;
      f.saveError = null;
      f.conflict = false;
    },

    // Modal: "Merge" path — caller (MergeEditor) already produced the merged text. Stage it
    // into draft, accept the incoming sha + contents as the new server baseline so the next
    // save lands cleanly, and clear the conflict marker. The editor stays dirty until the
    // operator hits Save through the normal toolbar.
    completeMerge(path: string, mergedText: string) {
      const f = this.open[path];
      if (!f || !f.incomingChange) return;
      const change = f.incomingChange;
      f.server = {
        path,
        sha256: change.newSha256,
        contents: change.newContents,
        lastModifiedUtc: new Date().toISOString(),
      };
      f.draft = mergedText;
      f.validation = null;
      f.incomingChange = null;
      f.saveError = null;
      f.conflict = false;
    },

    // Modal closer that doesn't take action (e.g. the diff view's "back" button). The next
    // save still 409s on the stale sha; the conflict modal will re-trigger on the new event.
    dismissIncomingChange(path: string) {
      const f = this.open[path];
      if (!f) return;
      f.incomingChange = null;
    },
  },
});
